import React, { useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Chip, CircularProgress
} from '@mui/material';
import { Description, Psychology, CheckCircle } from '@mui/icons-material';
import { getAIFeedbackSync } from '../../Utils/aiUtils';
import { DESIGN_PATTERN_REVIEW_PROMPT } from '../../Utils/aiPrompts';
import '../HLD/HLD.css';

const DESIGN_PATTERNS = [
  { name: 'Singleton',             category: 'Creational'    },
  { name: 'Factory',               category: 'Creational'    },
  { name: 'Abstract Factory',      category: 'Creational'    },
  { name: 'Builder',               category: 'Creational'    },
  { name: 'Prototype',             category: 'Creational'    },
  { name: 'Adapter',               category: 'Structural'    },
  { name: 'Decorator',             category: 'Structural'    },
  { name: 'Facade',                category: 'Structural'    },
  { name: 'Proxy',                 category: 'Structural'    },
  { name: 'Composite',             category: 'Structural'    },
  { name: 'Bridge',                category: 'Structural'    },
  { name: 'Flyweight',             category: 'Structural'    },
  { name: 'Observer',              category: 'Behavioral'    },
  { name: 'Strategy',              category: 'Behavioral'    },
  { name: 'Command',               category: 'Behavioral'    },
  { name: 'State',                 category: 'Behavioral'    },
  { name: 'Template Method',       category: 'Behavioral'    },
  { name: 'Chain of Responsibility', category: 'Behavioral'  },
  { name: 'Iterator',              category: 'Behavioral'    },
  { name: 'Mediator',              category: 'Behavioral'    },
  { name: 'Memento',               category: 'Behavioral'    },
  { name: 'Visitor',               category: 'Behavioral'    },
  { name: 'Repository',            category: 'Architectural' },
  { name: 'Service Layer',         category: 'Architectural' },
  { name: 'DAO',                   category: 'Architectural' },
];

const CATEGORIES = ['Creational', 'Structural', 'Behavioral', 'Architectural'];

const CHIP_COLORS = {
  Creational:    { bg: '#e8f5e9', border: '#a5d6a7', selected: '#2e7d32' },
  Structural:    { bg: '#e3f2fd', border: '#90caf9', selected: '#1565c0' },
  Behavioral:    { bg: '#fce4ec', border: '#f48fb1', selected: '#880e4f' },
  Architectural: { bg: '#fff3e0', border: '#ffcc80', selected: '#e65100' },
};

export default function DesignPatternQuestions({
  selectedPatterns = [],       // [{ pattern: 'Observer', usage: '' }, ...]
  onPatternsChange = () => {}, // (patterns) => void
  problemTitle = '',
  features = [],
  documentVisible = false,
  setDocumentVisible = () => {},
}) {
  const [aiFeedback, setAiFeedback] = useState('');
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);

  const isSelected = (name) => selectedPatterns.some(p => p.pattern === name);

  const togglePattern = (name) => {
    if (isSelected(name)) {
      onPatternsChange(selectedPatterns.filter(p => p.pattern !== name));
    } else {
      onPatternsChange([...selectedPatterns, { pattern: name, usage: '' }]);
    }
  };

  const updateUsage = (name, usage) => {
    onPatternsChange(
      selectedPatterns.map(p => p.pattern === name ? { ...p, usage } : p)
    );
  };

  const getAIFeedback = async () => {
    if (selectedPatterns.length === 0) return;
    setIsLoadingFeedback(true);
    setAiFeedback('');

    try {
      const userId =
        localStorage.getItem('userId') ||
        (JSON.parse(localStorage.getItem('profileObject') || '{}').id) ||
        'slazysloth_user';

      const featuresText =
        features.length > 0 ? features.join(', ') : 'Not specified';

      const patternsText = selectedPatterns
        .map(p => (p.usage ? `${p.pattern} (${p.usage})` : p.pattern))
        .join(', ');

      const systemPrompt = DESIGN_PATTERN_REVIEW_PROMPT(problemTitle, featuresText, patternsText);
      const userMessage = `Please review my design pattern choices.`;

      const response = await getAIFeedbackSync(
        systemPrompt,
        userMessage,
        userId,
        'anthropic',
        null,
        null,
        { maxRetries: 2 }
      );

      setAiFeedback(typeof response === 'string' ? response : JSON.stringify(response));
    } catch {
      setAiFeedback('Failed to get feedback. Please try again.');
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  return (
    <Box sx={{
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      p: 3,
      maxWidth: '1100px',
      mx: 'auto',
      backgroundColor: '#f5f5f5',
      gap: 2,
      overflowY: 'auto',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {problemTitle && (
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
            {problemTitle}
          </Typography>
        )}
        <Button
          variant="outlined"
          onClick={() => setDocumentVisible(!documentVisible)}
          startIcon={<Description />}
          size="small"
          sx={{
            borderColor: '#008080', color: '#008080',
            '&:hover': { backgroundColor: '#e3f2fd', borderColor: '#006666' },
          }}
        >
          {documentVisible ? 'Hide' : 'View Doc'}
        </Button>
      </Box>

      {/* Pattern picker */}
      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#ffffff' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 0.5, fontSize: '1rem' }}>
          Which design patterns did you use?
        </Typography>
        <Typography variant="body2" sx={{ color: '#888', mb: 2.5, fontSize: '0.85rem' }}>
          Click to select. Patterns are grouped by GoF category.
        </Typography>

        {CATEGORIES.map(category => {
          const colors = CHIP_COLORS[category];
          return (
            <Box key={category} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ color: '#999', fontWeight: 700, letterSpacing: 0.6,
                      textTransform: 'uppercase', mb: 1, display: 'block', fontSize: '0.7rem' }}
              >
                {category}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {DESIGN_PATTERNS.filter(p => p.category === category).map(({ name }) => {
                  const selected = isSelected(name);
                  return (
                    <Chip
                      key={name}
                      label={name}
                      onClick={() => togglePattern(name)}
                      variant={selected ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: selected ? colors.selected : 'transparent',
                        color: selected ? '#fff' : '#555',
                        borderColor: selected ? colors.selected : colors.border,
                        fontWeight: selected ? 700 : 400,
                        fontSize: '0.8rem',
                        height: 28,
                        '&:hover': {
                          backgroundColor: selected ? colors.selected : colors.bg,
                          borderColor: colors.selected,
                          color: selected ? '#fff' : colors.selected,
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Paper>

      {/* Usage fields + AI button */}
      {selectedPatterns.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, backgroundColor: '#ffffff' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
            Where did you apply each pattern?{' '}
            <Typography component="span" variant="caption" sx={{ color: '#aaa', fontWeight: 400 }}>
              (optional — helps AI give better feedback)
            </Typography>
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {selectedPatterns.map(({ pattern, usage }) => (
              <Box key={pattern} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  label={pattern}
                  size="small"
                  sx={{
                    backgroundColor: CHIP_COLORS[
                      DESIGN_PATTERNS.find(p => p.name === pattern)?.category || 'Creational'
                    ].selected,
                    color: '#fff',
                    fontWeight: 700,
                    minWidth: 130,
                    fontSize: '0.75rem',
                  }}
                />
                <TextField
                  size="small"
                  placeholder={`e.g. "Singleton for DatabaseConnection manager"`}
                  value={usage}
                  onChange={e => updateUsage(pattern, e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.85rem',
                      '& fieldset': { borderColor: '#e0e0e0' },
                      '&:hover fieldset': { borderColor: '#008080' },
                      '&.Mui-focused fieldset': { borderColor: '#008080' },
                    },
                  }}
                />
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Button
              variant="contained"
              onClick={getAIFeedback}
              disabled={isLoadingFeedback}
              startIcon={
                isLoadingFeedback
                  ? <CircularProgress size={14} color="inherit" />
                  : <Psychology />
              }
              sx={{
                backgroundColor: '#008080',
                textTransform: 'none',
                fontSize: '0.85rem',
                '&:hover': { backgroundColor: '#006666' },
                '&:disabled': { backgroundColor: '#e0e0e0', color: '#999' },
              }}
            >
              {isLoadingFeedback ? 'Getting feedback...' : 'Get AI Feedback'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* AI Feedback */}
      {aiFeedback && (
        <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f0fafa', borderLeft: '3px solid #008080' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <CheckCircle sx={{ color: '#008080', fontSize: '1rem' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#008080' }}>
              AI Feedback
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: '#333', lineHeight: 1.8, whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}
          >
            {aiFeedback}
          </Typography>
        </Paper>
      )}

      {/* Hint when nothing selected */}
      {selectedPatterns.length === 0 && (
        <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: '6px', borderLeft: '4px solid #1976d2' }}>
          <Typography variant="body2" sx={{ color: '#1976d2', fontStyle: 'italic', fontSize: '0.9rem' }}>
            Select the design patterns you applied in your solution. Optionally describe where each one fits,
            then click "Get AI Feedback" for a review.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
